import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { Logo } from "@/components/brand/logo";
import { LoadingScreen } from "@/components/LoadingScreen";
import { useSession } from "@/hooks/auth-hooks";

export const Route = createFileRoute("/cli-auth")({
	component: CliAuthPage,
});

type AuthState = "loading" | "authorizing" | "success" | "error";

function CliAuthPage() {
	const { session, user, isPending } = useSession();
	const [authState, setAuthState] = useState<AuthState>("loading");
	const [error, setError] = useState<string | null>(null);
	const [countdown, setCountdown] = useState(3);

	const code =
		typeof window !== "undefined"
			? new URLSearchParams(window.location.search).get("code")
			: null;

	const authorize = useCallback(async () => {
		if (!code) {
			setError("Missing authorization code.");
			setAuthState("error");
			return;
		}

		setAuthState("authorizing");

		try {
			const response = await fetch("/api/mcp/cli-authorize", {
				method: "POST",
				headers: {
					Accept: "application/json",
					"Content-Type": "application/json",
				},
				body: JSON.stringify({ code }),
			});

			const payload = await response.json().catch(() => null);

			if (!response.ok) {
				setError(payload?.error || "Failed to authorize.");
				setAuthState("error");
				return;
			}

			setAuthState("success");
		} catch {
			setError("Something went wrong. Please try again.");
			setAuthState("error");
		}
	}, [code]);

	useEffect(() => {
		if (isPending) return;

		if (!user || !session) {
			const redirectTarget = `/cli-auth?code=${code || ""}`;
			const redirectUrl = `/signup?redirect=${encodeURIComponent(redirectTarget)}`;
			window.location.assign(redirectUrl);
			return;
		}

		if (authState === "loading" && code) {
			authorize();
		}
	}, [isPending, user, session, authState, code, authorize]);

	useEffect(() => {
		if (authState !== "success") return;
		if (countdown <= 0) {
			window.location.assign("/dashboard");
			return;
		}
		const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
		return () => clearTimeout(timer);
	}, [authState, countdown]);

	if (isPending) return <LoadingScreen />;

	if (!code) {
		return (
			<AuthLayout>
				<StatusIcon state="error" />
				<h1 className="text-4xl font-semibold tracking-tight" style={{ color: "var(--km-on-surface)", fontFamily: "'Geist', -apple-system, sans-serif" }}>
					Invalid link
				</h1>
				<p className="text-md" style={{ color: "var(--km-on-surface-muted)" }}>
					This authorization link is missing a code. Run{" "}
					<InlineCode>npx agentframer@latest init</InlineCode>{" "}
					again.
				</p>
			</AuthLayout>
		);
	}

	if (authState === "loading" || authState === "authorizing") {
		return (
			<AuthLayout>
				<StatusIcon state="authorizing" />
				<h1 className="text-4xl font-semibold tracking-tight" style={{ color: "var(--km-on-surface)", fontFamily: "'Geist', -apple-system, sans-serif" }}>
					Connecting your agent…
				</h1>
				<p className="text-md" style={{ color: "var(--km-on-surface-muted)" }}>
					Generating your API key and linking it to your account.
				</p>
			</AuthLayout>
		);
	}

	if (authState === "error") {
		return (
			<AuthLayout>
				<StatusIcon state="error" />
				<h1 className="text-4xl font-semibold tracking-tight" style={{ color: "var(--km-on-surface)", fontFamily: "'Geist', -apple-system, sans-serif" }}>
					Authorization failed
				</h1>
				<p className="text-md" style={{ color: "var(--km-on-surface-muted)" }}>
					{error || "Something went wrong."}
				</p>
				<p className="text-sm" style={{ color: "var(--km-on-surface-muted)" }}>
					Run{" "}
					<InlineCode>npx agentframer@latest init</InlineCode>{" "}
					to try again.
				</p>
			</AuthLayout>
		);
	}

	return (
		<AuthLayout>
			<StatusIcon state="success" />
			<h1 className="text-4xl font-semibold tracking-tight" style={{ color: "var(--km-on-surface)", fontFamily: "'Geist', -apple-system, sans-serif" }}>
				Agent connected
			</h1>
			<p className="text-md" style={{ color: "var(--km-on-surface-muted)" }}>
				Your CLI has been authorized. Redirecting to dashboard in{" "}
				{countdown}s…
			</p>
			<Link
				to="/dashboard"
				className="inline-flex items-center gap-1.5 text-sm font-bold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
				style={{ color: "#6B3FF2", textDecoration: "none" }}
				onMouseEnter={e => { (e.currentTarget as HTMLElement).style.textDecoration = "underline dotted"; }}
				onMouseLeave={e => { (e.currentTarget as HTMLElement).style.textDecoration = "none"; }}
			onFocus={e => { (e.currentTarget as HTMLElement).style.textDecoration = "underline dotted"; }}
			onBlur={e => { (e.currentTarget as HTMLElement).style.textDecoration = "none"; }}
			>
				Go to dashboard now
				<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "16px" }}>arrow_forward</span>
			</Link>
		</AuthLayout>
	);
}

function InlineCode({ children }: { children: React.ReactNode }) {
	return (
		<code
			className="px-1.5 py-0.5 text-sm"
			style={{
				fontFamily: "'Geist Mono', 'JetBrains Mono', monospace",
				backgroundColor: "rgba(234,40,4,0.08)",
				color: "#6B3FF2",
				border: "1px solid rgba(234,40,4,0.15)",
				borderLeft: "2px solid #6B3FF2",
			}}
		>
			{children}
		</code>
	);
}

function AuthLayout({ children }: { children: React.ReactNode }) {
	return (
		<div
			className="relative"
			style={{ minHeight: "100svh", backgroundColor: "var(--km-surface)", color: "var(--km-on-surface)" }}
		>
			{/* Top brand bar */}
			<div className="absolute top-0 left-0 right-0 h-[3px]" style={{ background: "linear-gradient(to right, #6B3FF2, #5A33D4, #6B3FF2)" }} />
			{/* Subtle dot grid */}
			<div
				className="pointer-events-none absolute inset-0 opacity-[0.025]"
				style={{
					backgroundImage: "radial-gradient(circle, rgba(234,40,4,0.8) 1px, transparent 1px)",
					backgroundSize: "28px 28px",
				}}
				aria-hidden="true"
			/>
			{/* Radial glow */}
			<div
				className="pointer-events-none absolute inset-0"
				style={{
					background: "radial-gradient(ellipse 60% 50% at 50% 50%, rgba(234,40,4,0.05) 0%, transparent 70%)",
				}}
				aria-hidden="true"
			/>
			<main id="main-content" className="flex min-h-svh items-center justify-center px-4 sm:px-6">
				<div className="w-full max-w-lg">
					<div className="mb-8">
						<Logo size="sm" />
					</div>
					<div
						className="p-8 space-y-5"
						style={{
							backgroundColor: "var(--km-surface-low)",
							border: "1px solid rgba(255,255,255,0.08)",
							borderTop: "3px solid #6B3FF2",
						}}
					>
						<p className="text-[10px] font-bold uppercase tracking-[0.1em] mb-4" style={{ color: "#6B3FF2", fontFamily: "inherit" }}>CLI Authentication</p>
						{children}
					</div>
				</div>
			</main>
		</div>
	);
}

function StatusIcon({ state }: { state: AuthState }) {
	if (state === "success") {
		return (
			<div
				className="flex size-10 items-center justify-center"
				style={{ backgroundColor: "rgba(43,154,102,0.1)" }}
			>
				<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "20px", color: "#2b9a66", fontVariationSettings: "'FILL' 1" }}>check_circle</span>
			</div>
		);
	}
	if (state === "error") {
		return (
			<div
				className="flex size-10 items-center justify-center"
				style={{ backgroundColor: "rgba(255,110,132,0.1)" }}
			>
				<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "20px", color: "#ff6e84", fontVariationSettings: "'FILL' 1" }}>cancel</span>
			</div>
		);
	}
	return (
		<div
			className="flex size-10 items-center justify-center"
			style={{ backgroundColor: "var(--km-surface-high)" }}
		>
			<span
				className="material-symbols-outlined animate-spin"
				aria-hidden="true"
				style={{ fontSize: "20px", color: "var(--km-on-surface-muted)" }}
			>
				progress_activity
			</span>
		</div>
	);
}
